import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { fetchWithAuth } from '@/services/api';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const STORAGE_KEY = 'task-crm-support-chat-history';

interface SupportChatButtonProps {
  isCollapsed?: boolean;
  className?: string;
}

export function SupportChatButton({ isCollapsed = false, className }: SupportChatButtonProps) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Carregar histórico do localStorage no mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setMessages(JSON.parse(saved));
      } else {
        // Mensagem de boas-vindas
        setMessages([{
          role: 'assistant',
          content: 'Olá! Sou o assistente do Task CRM. Posso te ajudar a usar a plataforma. Pergunte onde fica algo, como fazer tal tarefa, etc.'
        }]);
      }
    } catch (e) {
      // localStorage corrompido, resetar
      setMessages([{
        role: 'assistant',
        content: 'Olá! Sou o assistente do Task CRM. Posso te ajudar a usar a plataforma. Pergunte onde fica algo, como fazer tal tarefa, etc.'
      }]);
    }
  }, []);

  // Salvar no localStorage quando messages muda
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
      } catch (e) {
        console.error('Erro ao salvar histórico de chat no localStorage:', e);
      }
    }
  }, [messages]);

  // Scroll pro fim quando nova msg
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    
    const newMessages = [...messages, { role: 'user' as const, content: userMessage }];
    setMessages(newMessages);
    setLoading(true);
    
    try {
      const res = await fetchWithAuth('/api/support/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: messages.slice(-6)
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setMessages([...newMessages, { role: 'assistant', content: data.reply }]);
      } else {
        toast.error(data.error || 'Erro no assistente');
        setMessages([...newMessages, { role: 'assistant', content: data.reply || 'Erro ao responder. Tente novamente.' }]);
      }
    } catch (err: any) {
      toast.error('Erro de conexão');
      setMessages([...newMessages, { role: 'assistant', content: 'Erro de conexão. Tente novamente.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error(e);
    }
    setMessages([{
      role: 'assistant',
      content: 'Histórico limpo. Como posso ajudar?'
    }]);
    toast.success('Histórico de chat limpo');
  };

  return (
    <>
      {/* Botão na Sidebar */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "flex items-center text-sm font-medium rounded-xl transition-colors relative cursor-pointer text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/70 hover:text-gray-900 dark:hover:text-white group",
          isCollapsed ? "justify-center h-10 w-10 mx-auto p-0" : "px-3 py-2.5 w-full",
          className
        )}
        aria-label="Assistente IA"
        title="Assistente IA"
      >
        <div className="relative flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-indigo-500 dark:text-indigo-400 group-hover:rotate-12 transition-transform" />
        </div>
        {!isCollapsed && (
          <span className="truncate flex-1 ml-3 text-left">Assistente IA</span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/30 md:hidden"
              onClick={() => setOpen(false)}
            />
            
            {/* Painel lateral direito */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-96 bg-white dark:bg-[#1A1D21] shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-blue-600 to-emerald-600 text-white shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    <div>
                      <h2 className="font-bold text-sm">Assistente Task CRM</h2>
                      <p className="text-[10px] opacity-80">Pergunte qualquer coisa</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleClearHistory}
                      className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                      title="Limpar histórico"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setOpen(false)}
                      className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                      title="Fechar"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Mensagens */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-3"
              >
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white rounded-br-sm shadow-sm'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-sm shadow-sm'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-2xl rounded-bl-sm shadow-sm">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="p-3 border-t border-gray-200 dark:border-gray-800 shrink-0 bg-white dark:bg-[#1A1D21]">
                <div className="flex items-end gap-2">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Pergunte algo..."
                    rows={1}
                    className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-0 rounded-xl text-sm resize-none focus:ring-2 focus:ring-blue-500/30 focus:outline-none"
                    style={{ maxHeight: '100px' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || loading}
                    className="p-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl disabled:opacity-50 transition-colors shrink-0 flex items-center justify-center"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between text-[10px] text-gray-400 dark:text-gray-500 mt-2 px-1">
                  <span>Assistente IA</span>
                  <span>Limite 20 msgs/hora</span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
